import { useState, useEffect } from 'react';
import { FormTemplate, FormField, MaintenanceUrgency, TemplateMeasurementConfig } from '@easydone/shared';
import {
  ExclamationCircleIcon,
  WrenchScrewdriverIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import { PhotoCapture } from './PhotoCapture';
import { SignatureCapture } from './SignatureCapture';

interface FormRendererProps {
  template: FormTemplate;
  onSubmit: (data: FormData) => void;
  initialData?: FormData;
  readOnly?: boolean;
  assetId?: string;
  flocId?: string;
  orgId?: string;
}

export interface FormData {
  templateId: string;
  templateVersion: number;
  sections: SectionData[];
  maintenanceRequired?: boolean;
  maintenanceUrgency?: MaintenanceUrgency;
  maintenanceNotes?: string;
}

interface SectionData {
  sectionId: string;
  fields: FieldData[];
  measurements?: MeasurementData[];
}

interface FieldData {
  fieldId: string;
  value: any;
  photos?: string[];
  signature?: string;
}

interface MeasurementData {
  measurementId: string;
  value: number | null;
}

export function FormRenderer({
  template,
  onSubmit,
  initialData,
  readOnly = false,
  assetId,
  flocId,
  orgId,
}: FormRendererProps) {
  const [formData, setFormData] = useState<FormData>(() => {
    if (initialData) {
      // Merge initialData with template to ensure all measurements are present
      return {
        ...initialData,
        sections: template.sections.map(section => {
          const existingSection = initialData.sections.find(s => s.sectionId === section.id);
          return {
            sectionId: section.id,
            fields: section.fields.map(field => {
              const existingField = existingSection?.fields.find(f => f.fieldId === field.id);
              return existingField || {
                fieldId: field.id,
                value: getDefaultValue(field),
              };
            }),
            measurements: section.measurements?.map(measurement => {
              const existingMeasurement = existingSection?.measurements?.find(m => m.measurementId === measurement.id);
              return existingMeasurement || {
                measurementId: measurement.id,
                value: null,
              };
            }) || [],
          };
        }),
      };
    }

    return {
      templateId: template.id,
      templateVersion: template.version,
      sections: template.sections.map(section => ({
        sectionId: section.id,
        fields: section.fields.map(field => ({
          fieldId: field.id,
          value: getDefaultValue(field),
        })),
        measurements: section.measurements?.map(measurement => ({
          measurementId: measurement.id,
          value: null,
        })) || [],
      })),
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getFieldValue = (sectionId: string, fieldId: string): any => {
    const section = formData.sections.find(s => s.sectionId === sectionId);
    const field = section?.fields.find(f => f.fieldId === fieldId);
    return field?.value;
  };

  const setFieldValue = (sectionId: string, fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.sectionId === sectionId
          ? {
              ...section,
              fields: section.fields.map(field =>
                field.fieldId === fieldId ? { ...field, value } : field
              ),
            }
          : section
      ),
    }));

    // Clear error for this field
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${sectionId}-${fieldId}`];
      return newErrors;
    });
  };

  const getMeasurementValue = (sectionId: string, measurementId: string): number | null => {
    const section = formData.sections.find(s => s.sectionId === sectionId);
    const measurement = section?.measurements?.find(m => m.measurementId === measurementId);
    return measurement?.value ?? null;
  };

  const setMeasurementValue = (sectionId: string, measurementId: string, value: number | null) => {
    console.log('setMeasurementValue called:', { sectionId, measurementId, value });

    setFormData(prev => {
      console.log('Previous formData:', prev);

      const updated = {
        ...prev,
        sections: prev.sections.map(section => {
          if (section.sectionId !== sectionId) return section;

          const measurements = section.measurements || [];
          const existingMeasurementIndex = measurements.findIndex(m => m.measurementId === measurementId);

          let updatedMeasurements: MeasurementData[];
          if (existingMeasurementIndex >= 0) {
            // Update existing measurement
            updatedMeasurements = measurements.map(m =>
              m.measurementId === measurementId ? { ...m, value } : m
            );
          } else {
            // Add new measurement if it doesn't exist
            updatedMeasurements = [...measurements, { measurementId, value }];
          }

          console.log('Updated measurements:', updatedMeasurements);

          return {
            ...section,
            measurements: updatedMeasurements,
          };
        }),
      };

      console.log('New formData:', updated);
      return updated;
    });

    // Clear error for this measurement
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${sectionId}-meas-${measurementId}`];
      return newErrors;
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    template.sections.forEach(section => {
      // Validate fields
      section.fields.forEach(field => {
        const value = getFieldValue(section.id, field.id);

        if (field.required && (value === null || value === undefined || value === '')) {
          newErrors[`${section.id}-${field.id}`] = `${field.label} is required`;
        }

        // Type-specific validation
        if (field.type === 'dropdown' || field.type === 'radio') {
          if (field.required && field.options && !field.options.includes(value)) {
            newErrors[`${section.id}-${field.id}`] = `Please select a valid option`;
          }
        }
      });

      // Validate measurements
      section.measurements?.forEach(measurement => {
        const value = getMeasurementValue(section.id, measurement.id);

        if (measurement.required && (value === null || value === undefined)) {
          newErrors[`${section.id}-meas-${measurement.id}`] = `${measurement.name} is required`;
        }

        // Validate numeric range
        if (value !== null && value !== undefined) {
          if (measurement.minValue !== undefined && value < measurement.minValue) {
            newErrors[`${section.id}-meas-${measurement.id}`] = `Value must be at least ${measurement.minValue}`;
          }
          if (measurement.maxValue !== undefined && value > measurement.maxValue) {
            newErrors[`${section.id}-meas-${measurement.id}`] = `Value must not exceed ${measurement.maxValue}`;
          }
        }
      });
    });

    // Validate maintenance tracking
    if (formData.maintenanceRequired && !formData.maintenanceUrgency) {
      alert('Please select an urgency level for the maintenance requirement.');
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="space-y-6">
      {/* Template Header */}
      <div className="card">
        <h1 className="text-2xl font-bold text-gray-900">{template.name}</h1>
        {template.description && (
          <p className="text-gray-600 mt-2">{template.description}</p>
        )}
        <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
          <span>Category: {template.category}</span>
          <span>Version: {template.version}</span>
        </div>
      </div>

      {/* Sections */}
      {template.sections.map(section => (
        <div key={section.id} className="card">
          <div className="border-b border-gray-200 pb-4 mb-4">
            <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
            {section.description && (
              <p className="text-gray-600 text-sm mt-1">{section.description}</p>
            )}
          </div>

          <div className="space-y-4">
            {section.fields.map(field => (
              <FieldRenderer
                key={field.id}
                field={field}
                value={getFieldValue(section.id, field.id)}
                onChange={value => setFieldValue(section.id, field.id, value)}
                error={errors[`${section.id}-${field.id}`]}
                readOnly={readOnly}
              />
            ))}

            {section.measurements && section.measurements.length > 0 && (
              <>
                {section.measurements.map(measurement => (
                  <MeasurementRenderer
                    key={measurement.id}
                    measurement={measurement}
                    value={getMeasurementValue(section.id, measurement.id)}
                    onChange={value => setMeasurementValue(section.id, measurement.id, value)}
                    error={errors[`${section.id}-meas-${measurement.id}`]}
                    readOnly={readOnly}
                    assetId={assetId}
                    flocId={flocId}
                    orgId={orgId}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      ))}

      {/* Maintenance Tracking */}
      {!readOnly && (
        <div className="card bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3 mb-4">
            <WrenchScrewdriverIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Maintenance Required?
              </h3>
              <p className="text-sm text-gray-600">
                Flag if this inspection identified any maintenance needs
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.maintenanceRequired || false}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  maintenanceRequired: e.target.checked,
                  ...((!e.target.checked) && { maintenanceUrgency: undefined, maintenanceNotes: undefined })
                }))}
                className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
              />
              <span className="ml-3 text-sm font-medium text-gray-900">
                Maintenance is required for this asset
              </span>
            </label>

            {formData.maintenanceRequired && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Urgency Level <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={formData.maintenanceUrgency || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      maintenanceUrgency: e.target.value as MaintenanceUrgency
                    }))}
                    className="input w-full"
                    required
                  >
                    <option value="">Select urgency level...</option>
                    <option value="not_in_use">Not in use</option>
                    <option value="very_low">Very low - Minimal priority</option>
                    <option value="low">Low - Can be scheduled for routine maintenance</option>
                    <option value="medium">Medium - Should be addressed soon</option>
                    <option value="high">High - Requires prompt attention</option>
                    <option value="very_high">Very high - Immediate action required</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maintenance Notes
                  </label>
                  <textarea
                    value={formData.maintenanceNotes || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      maintenanceNotes: e.target.value
                    }))}
                    className="input w-full"
                    rows={3}
                    placeholder="Describe the maintenance issue or recommended action..."
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Submit Button */}
      {!readOnly && (
        <div className="flex justify-end gap-3">
          <button onClick={handleSubmit} className="btn btn-primary">
            Submit Inspection
          </button>
        </div>
      )}
    </div>
  );
}

interface FieldRendererProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  readOnly?: boolean;
}

function FieldRenderer({ field, value, onChange, error, readOnly }: FieldRendererProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        {field.required && <span className="text-red-600 ml-1">*</span>}
      </label>

      {field.helpText && (
        <p className="text-xs text-gray-500 mb-2">{field.helpText}</p>
      )}

      <div className="relative">
        {renderFieldInput(field, value, onChange, readOnly)}
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
          <ExclamationCircleIcon className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

interface MeasurementRendererProps {
  measurement: TemplateMeasurementConfig;
  value: number | null;
  onChange: (value: number | null) => void;
  error?: string;
  readOnly?: boolean;
  assetId?: string;
  flocId?: string;
  orgId?: string;
}

interface LastReading {
  value: number;
  timestamp: Date;
  status?: 'normal' | 'warning' | 'critical';
}

function MeasurementRenderer({ measurement, value, onChange, error, readOnly, assetId, flocId, orgId }: MeasurementRendererProps) {
  const [lastReading, setLastReading] = useState<LastReading | null>(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingValue, setPendingValue] = useState<number | null>(null);
  const [changePercent, setChangePercent] = useState<number | null>(null);

  // Load last measurement reading
  useEffect(() => {
    const loadLastReading = async () => {
      if (!orgId || (!assetId && !flocId)) return;

      setLoading(true);
      try {
        const { getLatestMeasurementReading } = await import('../services/measurementService');
        const entityType = assetId ? 'asset' : 'floc';
        const entityId = assetId || flocId || '';

        const reading = await getLatestMeasurementReading(
          orgId,
          entityType,
          entityId,
          measurement.id
        );

        if (reading && typeof reading.value === 'number') {
          // Convert Firestore Timestamp to Date
          let timestamp: Date;
          if (reading.readingDate instanceof Date) {
            timestamp = reading.readingDate;
          } else if (reading.readingDate && typeof (reading.readingDate as any).toDate === 'function') {
            timestamp = (reading.readingDate as any).toDate();
          } else if (reading.readingDate && typeof (reading.readingDate as any).seconds === 'number') {
            timestamp = new Date((reading.readingDate as any).seconds * 1000);
          } else {
            timestamp = new Date();
          }

          setLastReading({
            value: reading.value,
            timestamp,
            status: reading.status,
          });
        }
      } catch (error) {
        console.error('Failed to load last measurement reading:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLastReading();
  }, [orgId, assetId, flocId, measurement.id]);

  const handleValueChange = (newValue: number | null) => {
    console.log('MeasurementRenderer: handleValueChange called with:', newValue);

    // Always update the input field immediately so user sees what they typed
    onChange(newValue);

    // Check if we need to show confirmation dialog
    if (newValue === null || !lastReading || !measurement.maxChangePercent) {
      return;
    }

    // Calculate percentage change
    const percentChange = Math.abs(((newValue - lastReading.value) / lastReading.value) * 100);

    if (percentChange > measurement.maxChangePercent) {
      // Show confirmation dialog
      setPendingValue(newValue);
      setChangePercent(percentChange);
      setShowConfirmDialog(true);
    }
  };

  const confirmChange = () => {
    // Value is already set, just close the dialog
    setShowConfirmDialog(false);
    setPendingValue(null);
    setChangePercent(null);
  };

  const cancelChange = () => {
    // Revert to null or last reading value
    onChange(null);
    setShowConfirmDialog(false);
    setPendingValue(null);
    setChangePercent(null);
  };
  const getStatusColor = () => {
    if (value === null || value === undefined) return '';

    // Check critical thresholds
    if (measurement.criticalThreshold) {
      if (measurement.criticalThreshold.min !== undefined && value < measurement.criticalThreshold.min) {
        return 'border-red-500 bg-red-50';
      }
      if (measurement.criticalThreshold.max !== undefined && value > measurement.criticalThreshold.max) {
        return 'border-red-500 bg-red-50';
      }
    }

    // Check warning thresholds
    if (measurement.warningThreshold) {
      if (measurement.warningThreshold.min !== undefined && value < measurement.warningThreshold.min) {
        return 'border-yellow-500 bg-yellow-50';
      }
      if (measurement.warningThreshold.max !== undefined && value > measurement.warningThreshold.max) {
        return 'border-yellow-500 bg-yellow-50';
      }
    }

    return 'border-green-500 bg-green-50';
  };

  return (
    <>
      <div className={`rounded-lg p-4 border-2 ${getStatusColor() || 'border-blue-200 bg-blue-50'}`}>
        <div className="flex items-start gap-3">
          <BeakerIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-900 mb-1">
              {measurement.name}
              {measurement.required && <span className="text-red-600 ml-1">*</span>}
            </label>

            {measurement.description && (
              <p className="text-xs text-gray-600 mb-2">{measurement.description}</p>
            )}

            {/* Last Reading Display */}
            {lastReading && (
              <div className="mb-3 p-2 bg-gray-100 rounded text-xs">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Last Reading:</span>
                  <span className="text-gray-900">
                    {lastReading.value} {measurement.unit}
                    <span className="text-gray-500 ml-2">
                      ({new Date(lastReading.timestamp).toLocaleDateString()} {new Date(lastReading.timestamp).toLocaleTimeString()})
                    </span>
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="number"
                value={value ?? ''}
                onChange={e => handleValueChange(e.target.value ? Number(e.target.value) : null)}
                placeholder={`Enter ${measurement.name.toLowerCase()}`}
                disabled={readOnly || loading}
                step={measurement.precision ? Math.pow(10, -measurement.precision) : 'any'}
                min={measurement.minValue}
                max={measurement.maxValue}
                className={`input flex-1 ${readOnly ? 'bg-gray-100' : ''}`}
              />
              {measurement.unit && (
                <span className="text-sm font-medium text-gray-700 px-2 py-1 bg-white border border-gray-300 rounded">
                  {measurement.unit}
                </span>
              )}
            </div>

            {/* Threshold indicators */}
            {(measurement.warningThreshold || measurement.criticalThreshold) && (
              <div className="mt-2 text-xs text-gray-600 space-y-1">
                {measurement.warningThreshold && (
                  <div className="flex gap-2">
                    <span className="text-yellow-600">⚠ Warning:</span>
                    <span>
                      {measurement.warningThreshold.min !== undefined && `Min: ${measurement.warningThreshold.min}`}
                      {measurement.warningThreshold.min !== undefined && measurement.warningThreshold.max !== undefined && ' | '}
                      {measurement.warningThreshold.max !== undefined && `Max: ${measurement.warningThreshold.max}`}
                    </span>
                  </div>
                )}
                {measurement.criticalThreshold && (
                  <div className="flex gap-2">
                    <span className="text-red-600">🔴 Critical:</span>
                    <span>
                      {measurement.criticalThreshold.min !== undefined && `Min: ${measurement.criticalThreshold.min}`}
                      {measurement.criticalThreshold.min !== undefined && measurement.criticalThreshold.max !== undefined && ' | '}
                      {measurement.criticalThreshold.max !== undefined && `Max: ${measurement.criticalThreshold.max}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="flex items-center gap-1 mt-2 text-sm text-red-600">
                <ExclamationCircleIcon className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Unusual Reading Detected
            </h3>
            <p className="text-gray-700 mb-4">
              The new reading of <strong>{pendingValue} {measurement.unit}</strong> differs from the last reading
              of <strong>{lastReading?.value} {measurement.unit}</strong> by <strong>{changePercent?.toFixed(1)}%</strong>.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              This exceeds the configured threshold of {measurement.maxChangePercent}%.
              Please confirm this reading is correct.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelChange}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmChange}
                className="btn btn-primary"
              >
                Confirm Reading
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function renderFieldInput(
  field: FormField,
  value: any,
  onChange: (value: any) => void,
  readOnly?: boolean
) {
  const commonProps = {
    disabled: readOnly,
    className: `input w-full ${readOnly ? 'bg-gray-100' : ''}`,
  };

  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder}
          {...commonProps}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder}
          {...commonProps}
        />
      );

    case 'textarea':
      return (
        <textarea
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={4}
          {...commonProps}
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          {...commonProps}
        />
      );

    case 'time':
      return (
        <input
          type="time"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          {...commonProps}
        />
      );

    case 'dropdown':
      return (
        <select
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          {...commonProps}
        >
          <option value="">Select an option...</option>
          {field.options?.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );

    case 'radio':
      return (
        <div className="space-y-2">
          {field.options?.map(option => (
            <label key={option} className="flex items-center">
              <input
                type="radio"
                name={field.id}
                value={option}
                checked={value === option}
                onChange={e => onChange(e.target.value)}
                disabled={readOnly}
                className="mr-2"
              />
              <span className="text-sm">{option}</span>
            </label>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <div className="space-y-2">
          {field.options?.map(option => (
            <label key={option} className="flex items-center">
              <input
                type="checkbox"
                checked={Array.isArray(value) && value.includes(option)}
                onChange={e => {
                  const currentValue = Array.isArray(value) ? value : [];
                  const newValue = e.target.checked
                    ? [...currentValue, option]
                    : currentValue.filter(v => v !== option);
                  onChange(newValue);
                }}
                disabled={readOnly}
                className="mr-2"
              />
              <span className="text-sm">{option}</span>
            </label>
          ))}
        </div>
      );

    case 'photo':
      return (
        <PhotoCapture
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          maxPhotos={field.config?.maxPhotos || 5}
          disabled={readOnly}
        />
      );

    case 'signature':
      return (
        <SignatureCapture
          value={value}
          onChange={onChange}
          disabled={readOnly}
        />
      );

    default:
      return (
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          {...commonProps}
        />
      );
  }
}

function getDefaultValue(field: FormField): any {
  switch (field.type) {
    case 'checkbox':
      return [];
    case 'photo':
      return [];
    case 'number':
      return '';
    default:
      return '';
  }
}
